import React, { useEffect, useState, useMemo } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import { DATE_FORMAT, formatApiDate } from "../../helpers/dateFormat";
import ConfirmModal, { MODAL_SIZES } from "../../components/ConfirmModal";
import callApi from "../../helpers/CallApi";
import {
  getLoanList,
  getLoan,
  getNextDepositReceiptNumber,
  createDeposit,
  getBankAccountList,
} from "../../helpers/API";
import { toast } from "react-toastify";

const NewDepositModal = ({ show, toggle, onSaved }) => {
  const { t } = useTranslation();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nextReceiptNumber, setNextReceiptNumber] = useState("Test123");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formValuesToConfirm, setFormValuesToConfirm] = useState(null);
  const [loadingLoan, setLoadingLoan] = useState(false);

  const initialValues = useMemo(
    () => ({
      loanNumber: "",
      loanId: "",
      customerDisplay: "",
      receiptNumber: nextReceiptNumber,
      date: new Date(),
      principalDue: "",
      interestDue: "",
      installmentDate: null,
      totalDue: "",
      paymentType: "cash",
      bankAccountId: "",
      bankName: "",
      chequeNumber: "",
      includeInterest: true,
    }),
    [nextReceiptNumber]
  );

  useEffect(() => {
    if (show) {
      callApi({
        callback: getNextDepositReceiptNumber,
        payload: [],
        onSuccess: (res) => {
          setNextReceiptNumber(res?.receiptNumber ?? res?.data?.receiptNumber ?? "");
        },
        onError: () => setNextReceiptNumber(""),
      });
      callApi({
        callback: getBankAccountList,
        payload: [{}],
        onSuccess: (res) => {
          setBankAccounts(res?.data ?? res ?? []);
        },
        onError: () => setBankAccounts([]),
      });
    }
  }, [show]);

  const validationSchema = Yup.object({
    loanNumber: Yup.string().required(t("deposit.form.validation.loanNumberRequired")),
    receiptNumber: Yup.string().required(t("deposit.form.validation.receiptNumberRequired")),
    date: Yup.date().required(t("deposit.form.validation.dateRequired")),
    principalDue: Yup.number()
      .min(0, t("deposit.form.validation.principalMin"))
      .required(t("deposit.form.validation.principalRequired")),
    interestDue: Yup.number().min(0).nullable(),
    totalDue: Yup.number()
      .min(0, t("deposit.form.validation.totalMin"))
      .required(t("deposit.form.validation.totalRequired")),
    paymentType: Yup.string().oneOf(["cash", "bank"]).required(),
    bankAccountId: Yup.string().when("paymentType", {
      is: "bank",
      then: (schema) => schema.required(t("deposit.form.validation.bankRequired")),
    }),
    chequeNumber: Yup.string().when("paymentType", {
      is: "bank",
      then: (schema) => schema.required(t("deposit.form.validation.chequeNumberRequired")),
    }),
  });
  function generateSchedule({
    loanAmount,
    annualRate,
    emi,
    tenureMonths,
    interestType = "reducing", // "flat" or "reducing"
    totalInterest = null,
  }) {
    let balance = Number(loanAmount);
    emi = Number(emi);

    const schedule = [];

    // 👉 FLAT INTEREST LOGIC
    if (interestType === "flat" && totalInterest !== null) {
      const monthlyPrincipal = balance / tenureMonths;
      const monthlyInterest = totalInterest / tenureMonths;

      for (let month = 1; month <= tenureMonths; month++) {
        balance -= monthlyPrincipal;

        schedule.push({
          month,
          emi: emi.toFixed(2),
          interest: monthlyInterest.toFixed(2),
          principal: monthlyPrincipal.toFixed(2),
          remainingBalance: Math.max(0, balance.toFixed(2)),
        });
      }

      return schedule;
    }

    // 👉 REDUCING BALANCE LOGIC
    const monthlyRate = annualRate / 12 / 100;

    for (let month = 1; month <= tenureMonths; month++) {
      const interest = balance * monthlyRate;
      let principal = emi - interest;

      // last EMI adjustment
      if (month === tenureMonths) {
        principal = balance;
        emi = principal + interest;
        balance = 0;
      } else {
        balance -= principal;
      }

      schedule.push({
        month,
        emi: emi.toFixed(2),
        interest: interest.toFixed(2),
        principal: principal.toFixed(2),
        remainingBalance: Math.max(0, balance.toFixed(2)),
      });

      if (balance <= 0) break;
    }

    return schedule;
  }
  function calculateCurrentEMIBreakdown(data) {
    const {
      loanAmount,
      interestPerYear,
      monthlyInstallmentAmount, // This is the fixed EMI
      receivedPrinciple // Total principal paid so far from DB
    } = data;

    // 1. Find the current outstanding balance
    const currentOutstandingBalance = loanAmount - receivedPrinciple;

    // 2. Calculate this month's interest portion
    // Formula: (Balance * Rate) / 12
    const monthlyRate = (interestPerYear / 100) / 12;
    const interestForThisMonth = currentOutstandingBalance * monthlyRate;

    // 3. Calculate this month's principal portion
    // The EMI is fixed, so the rest goes to principal
    let principalForThisMonth = monthlyInstallmentAmount - interestForThisMonth;

    // 4. Edge case: Ensure principal doesn't exceed remaining balance
    if (principalForThisMonth > currentOutstandingBalance) {
      principalForThisMonth = currentOutstandingBalance;
    }

    return {
      interestComponent: parseFloat(interestForThisMonth.toFixed(2)),
      principalComponent: parseFloat(principalForThisMonth.toFixed(2)),
      remainingBalanceAfterPayment: parseFloat((currentOutstandingBalance - principalForThisMonth).toFixed(2))
    };
  }

  const loadLoanByLoanNumber = (loanNumber, setFieldValue) => {
    if (!loanNumber || !loanNumber.trim()) return;
    setLoadingLoan(true);
    getLoanList({ loanNumber: loanNumber.trim() })
      .then((res) => {
        const list = res?.data ?? res ?? [];
        const loan = Array.isArray(list) ? list.find((l) => (l.loanNumber || l.billNo || "") === loanNumber.trim()) : null;
        const first = Array.isArray(list) ? list[0] : null;
        const toFetch = loan ? loan : first;
        if (!toFetch?.id) {
          toast.error(t("deposit.form.errors.loanNotFound"));
          setLoadingLoan(false);
          return;
        }
        return getLoan(toFetch.id);
      })
      .then((loanRes) => {
        if (!loanRes) {
          setLoadingLoan(false);
          return;
        }
        const borrower = loanRes.borrower || {};
        const name = borrower.name || "";

        if (loanRes?.monthlyInstallment) {
          const {
            interestComponent,
            principalComponent,
          } = calculateCurrentEMIBreakdown({
            loanAmount: loanRes?.loanAmount,
            interestPerYear: loanRes?.interestPerYear,
            monthlyInstallmentAmount: loanRes?.monthlyInstallmentAmount,
            receivedPrinciple: loanRes?.receivedPrinciple || 0,
          })
          setFieldValue("principalDue", principalComponent > 0 ? principalComponent?.toFixed(2) : "0");
          setFieldValue("interestDue", interestComponent > 0 ? interestComponent?.toFixed(2) : "0");
          setFieldValue("totalDue", (principalComponent + interestComponent)?.toFixed(2));
        } else {
          setFieldValue("principalDue", Number(loanRes?.loanAmount) || 0);
          setFieldValue("interestDue", Number(loanRes?.totalInterest) || 0);
          setFieldValue("totalDue", Number(loanRes?.loanAmount) + Number(loanRes?.totalInterest) || 0);
        }
        setFieldValue("loanId", loanRes.id);
        setFieldValue("customerDisplay", name);
        setFieldValue("date", new Date());
        if (loanRes.firstEMIDate) setFieldValue("installmentDate", new Date(loanRes.firstEMIDate));
        setLoadingLoan(false);
      })
      .catch(() => {
        toast.error(t("deposit.form.errors.loadLoanFailed"));
        setLoadingLoan(false);
      });
  };

  const handleSubmit = (values) => {
    setFormValuesToConfirm(values);
    setShowConfirmation(true);
  };

  const handleConfirmedSave = () => {
    const values = formValuesToConfirm;
    if (!values) return;
    const payload = {
      loanId: values.loanId,
      receiptNumber: values.receiptNumber,
      date: formatApiDate(values.date),
      principalAmount: Number(values.principalDue) || 0,
      interestAmount: Number(values.interestDue) || 0,
      totalAmount: Number(values.totalDue) || 0,
      paymentType: values.paymentType,
    };
    if (values.paymentType === "bank") {
      payload.bankAccountId = values.bankAccountId;
      payload.chequeNumber = values.chequeNumber;
    }
    callApi({
      callback: createDeposit,
      payload: [payload],
      onSuccess: () => {
        toast.success(t("deposit.form.messages.createSuccess"));
        setShowConfirmation(false);
        setFormValuesToConfirm(null);
        onSaved?.();
        toggle();
      },
      onError: (msg) => toast.error(msg || t("deposit.form.messages.createError")),
    });
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setFormValuesToConfirm(null);
    toggle();
  };

  const updateTotalDue = (setFieldValue, principal, interest) => {
    const p = Number(principal) || 0;
    const i = Number(interest) || 0;
    setFieldValue("totalDue", (p + i).toString());
  };

  return (
    <>
      <Modal isOpen={show} toggle={handleClose} size="lg" centered>
        <ModalHeader toggle={handleClose}>
          {t("deposit.form.title")}
        </ModalHeader>
        <ModalBody>
          <Formik
            key={show ? "open" : "closed"}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.loanNumber")}</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          name="loanNumber"
                          value={values.loanNumber}
                          onChange={(e) => setFieldValue("loanNumber", e.target.value)}
                          onBlur={(e) => {
                            handleBlur(e);
                            loadLoanByLoanNumber(e.target.value, setFieldValue);
                          }}
                          placeholder={t("deposit.form.placeholders.loanNumber")}
                          isInvalid={touched.loanNumber && !!errors.loanNumber}
                        />
                        <Button
                          type="button"
                          variant="outline-primary"
                          onClick={() => loadLoanByLoanNumber(values.loanNumber, setFieldValue)}
                          disabled={loadingLoan}
                        >
                          {loadingLoan ? t("deposit.form.loading") : t("deposit.form.loadLoan")}
                        </Button>
                      </div>
                      <ErrorMessage name="loanNumber" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("deposit.form.customerDisplay")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.customerDisplay}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.receiptNumber")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="receiptNumber"
                        value={values.receiptNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        readOnly
                        className="bg-light"
                        isInvalid={touched.receiptNumber && !!errors.receiptNumber}
                      />
                      <ErrorMessage name="receiptNumber" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.date")}</Form.Label>
                      <DatePicker
                        wrapperClassName="w-100"
                        selected={values.date}
                        onChange={(date) => setFieldValue("date", date)}
                        dateFormat={DATE_FORMAT.DISPLAY}
                        className={`form-control ${touched.date && errors.date ? "is-invalid" : ""}`}
                      />
                      <ErrorMessage name="date" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.principalDue")}</Form.Label>
                      <Form.Control
                        type="number"
                        disabled
                        name="principalDue"
                        className="bg-light"
                        value={values.principalDue}
                        onChange={(e) => {
                          setFieldValue("principalDue", e.target.value);
                          updateTotalDue(setFieldValue, e.target.value, values.interestDue);
                        }}
                        onBlur={handleBlur}
                        step="0.01"
                        min="0"
                        isInvalid={touched.principalDue && !!errors.principalDue}
                      />
                      <ErrorMessage name="principalDue" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("deposit.form.interestDue")}</Form.Label>
                      <Form.Control
                        type="number"
                        disabled
                        name="interestDue"
                        className="bg-light"
                        value={values.interestDue}
                        onChange={(e) => {
                          setFieldValue("interestDue", e.target.value);
                          updateTotalDue(setFieldValue, values.principalDue, e.target.value);
                        }}
                        onBlur={handleBlur}
                        step="0.01"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.totalDue")}</Form.Label>
                      <Form.Control
                        type="number"
                        disabled
                        name="totalDue"
                        className="bg-light"
                        value={values.totalDue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        step="0.01"
                        min="0"
                        isInvalid={touched.totalDue && !!errors.totalDue}
                      />
                      <ErrorMessage name="totalDue" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  </Col>
                </Row>



                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">{t("deposit.form.paymentType")}</Form.Label>
                      <div>
                        <input
                          type="radio"
                          id="paymentType-cash"
                          name="paymentType"
                          value="cash"
                          checked={values.paymentType === "cash"}
                          onClick={() => {
                            setFieldValue("paymentType", "cash", true);
                            setFieldTouched("paymentType", true, true);
                          }}
                          onBlur={() => setFieldTouched("paymentType", true, true)}
                          className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
                        />
                        <label htmlFor="paymentType-cash" className="form-check-label me-3">
                          {t("deposit.form.paymentTypeCash")}
                        </label>
                        <input
                          type="radio"
                          id="paymentType-bank"
                          name="paymentType"
                          value="bank"
                          checked={values.paymentType === "bank"}
                          onClick={() => {
                            setFieldValue("paymentType", "bank", true);
                            setFieldTouched("paymentType", true, true);
                          }}
                          onBlur={() => setFieldTouched("paymentType", true, true)}
                          className={`form-check-input me-2 ${touched.paymentType && errors.paymentType ? "is-invalid" : ""}`}
                        />
                        <label htmlFor="paymentType-bank" className="form-check-label">
                          {t("deposit.form.paymentTypeBank")}
                        </label>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {values.paymentType === "bank" && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">{t("deposit.form.bank")}</Form.Label>
                        <Form.Select
                          name="bankAccountId"
                          value={values.bankAccountId}
                          onChange={(e) => {
                            const id = e.target.value;
                            const acc = bankAccounts.find((a) => String(a.id) === String(id));
                            setFieldValue("bankAccountId", id);
                            setFieldValue("bankName", acc?.bankName ?? "");
                          }}
                          onBlur={handleBlur}
                          isInvalid={touched.bankAccountId && !!errors.bankAccountId}
                        >
                          <option value="">{t("deposit.form.selectBank")}</option>
                          {bankAccounts?.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.bankName || acc.accountHolderName || acc.accountNumber}
                            </option>
                          ))}
                        </Form.Select>
                        <ErrorMessage name="bankAccountId" component="div" className="text-danger small mt-1" />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="required">{t("deposit.form.chequeNumber")}</Form.Label>
                        <Form.Control
                          type="text"
                          name="chequeNumber"
                          value={values.chequeNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={t("deposit.form.placeholders.chequeNumber")}
                          isInvalid={touched.chequeNumber && !!errors.chequeNumber}
                        />
                        <ErrorMessage name="chequeNumber" component="div" className="text-danger small mt-1" />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <ModalFooter className="p-0 pt-3 border-0">
                  <Button type="button" variant="secondary" onClick={handleClose}>
                    {t("deposit.form.cancel")}
                  </Button>
                  <Button type="submit" variant="primary">
                    {t("deposit.form.save")}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={showConfirmation}
        toggle={() => {
          setShowConfirmation(false);
          setFormValuesToConfirm(null);
        }}
        title={t("deposit.confirmation.title")}
        size={MODAL_SIZES.LARGE}
        confirmLabel={t("deposit.confirmation.confirmLabel")}
        cancelLabel={t("deposit.confirmation.cancelLabel")}
        onConfirm={handleConfirmedSave}
        confirmColor="primary"
      >
        {formValuesToConfirm && (
          <div className="mb-3">
            <p><strong>{t("deposit.confirmation.loanNo")}:</strong> {formValuesToConfirm.loanNumber}</p>
            <p><strong>{t("deposit.confirmation.customerName")}:</strong> {formValuesToConfirm.customerDisplay}</p>
            <p><strong>{t("deposit.confirmation.principalPaid")}:</strong> ₹{Number(formValuesToConfirm.principalDue || 0).toLocaleString("en-IN")}</p>
            <p><strong>{t("deposit.confirmation.interestPaid")}:</strong> ₹{Number(formValuesToConfirm.interestDue || 0).toLocaleString("en-IN")}</p>
            <p><strong>{t("deposit.confirmation.totalPaid")}:</strong> ₹{Number(formValuesToConfirm.totalDue || 0).toLocaleString("en-IN")}</p>
            <p className="text-muted small mt-2">{t("deposit.confirmation.message")}</p>
          </div>
        )}
      </ConfirmModal>
    </>
  );
};

export default NewDepositModal;
